import * as React from "react";
import useEmblaCarousel, {
  type UseEmblaCarouselType,
} from "embla-carousel-react";
import { ChevronLeft, ChevronRight, LucideIcon } from "lucide-react";
import { cn } from "../utils";
import { Button } from "./button";

type CarouselApi = UseEmblaCarouselType[1];
type UseCarouselParameters = Parameters<typeof useEmblaCarousel>;
type CarouselOptions = UseCarouselParameters[0];
type CarouselPlugin = UseCarouselParameters[1];

type CarouselProps = {
  opts?: CarouselOptions;
  plugins?: CarouselPlugin;
  orientation?: "horizontal" | "vertical";
  setApi?: (api: CarouselApi) => void;
};

type CarouselContextProps = {
  carouselRef: ReturnType<typeof useEmblaCarousel>[0];
  api: ReturnType<typeof useEmblaCarousel>[1];
  scrollPrev: () => void;
  scrollNext: () => void;
  scrollTo: (index: number) => void;
  canScrollPrev: boolean;
  canScrollNext: boolean;
} & CarouselProps;

type CarouselFooterContextProps = {
  isInFooter: boolean;
};

const CarouselContext = React.createContext<CarouselContextProps | null>(null);
const CarouselFooterContext =
  React.createContext<CarouselFooterContextProps | null>(null);

function useCarousel() {
  const context = React.useContext(CarouselContext);

  if (!context) {
    throw new Error("useCarousel must be used within a <Carousel />");
  }

  return context;
}

function Carousel({
  orientation = "horizontal",
  opts,
  setApi,
  plugins,
  className,
  children,
  ...props
}: React.ComponentProps<"div"> & CarouselProps) {
  const [carouselRef, api] = useEmblaCarousel(
    {
      ...opts,
      axis: orientation === "horizontal" ? "x" : "y",
    },
    plugins,
  );
  const [canScrollPrev, setCanScrollPrev] = React.useState(false);
  const [canScrollNext, setCanScrollNext] = React.useState(false);

  const onSelect = React.useCallback((api: CarouselApi) => {
    if (!api) return;
    setCanScrollPrev(api.canScrollPrev());
    setCanScrollNext(api.canScrollNext());
  }, []);

  const scrollPrev = React.useCallback(() => {
    api?.scrollPrev();
  }, [api]);

  const scrollNext = React.useCallback(() => {
    api?.scrollNext();
  }, [api]);

  const scrollTo = React.useCallback(
    (index: number) => {
      api?.scrollTo(index);
    },
    [api],
  );

  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        scrollPrev();
      } else if (event.key === "ArrowRight") {
        event.preventDefault();
        scrollNext();
      }
    },
    [scrollPrev, scrollNext],
  );

  React.useEffect(() => {
    if (!api || !setApi) return;
    setApi(api);
  }, [api, setApi]);

  React.useEffect(() => {
    if (!api) return;
    onSelect(api);
    api.on("reInit", onSelect);
    api.on("select", onSelect);

    return () => {
      api?.off("reInit", onSelect);
      api?.off("select", onSelect);
    };
  }, [api, onSelect]);

  return (
    <CarouselContext.Provider
      value={{
        carouselRef,
        api: api,
        opts,
        orientation:
          orientation || (opts?.axis === "y" ? "vertical" : "horizontal"),
        scrollPrev,
        scrollNext,
        scrollTo,
        canScrollPrev,
        canScrollNext,
      }}
    >
      <div
        onKeyDownCapture={handleKeyDown}
        className={cn("qodo qodo-carousel", "relative", className)}
        role="region"
        aria-roledescription="carousel"
        data-slot="carousel"
        {...props}
      >
        {children}
      </div>
    </CarouselContext.Provider>
  );
}

function CarouselContent({ className, ...props }: React.ComponentProps<"div">) {
  const { carouselRef, orientation } = useCarousel();

  return (
    <div
      ref={carouselRef}
      className="qodo qodo-carousel-content overflow-hidden"
      data-slot="carousel-content"
    >
      <div
        className={cn(
          "flex",
          orientation === "horizontal" ? "-ml-4" : "-mt-4 flex-col",
          className,
        )}
        {...props}
      />
    </div>
  );
}

function CarouselItem({ className, ...props }: React.ComponentProps<"div">) {
  const { orientation } = useCarousel();

  return (
    <div
      role="group"
      aria-roledescription="slide"
      data-slot="carousel-item"
      className={cn(
        "qodo qodo-carousel-item min-w-0 shrink-0 grow-0 basis-full",
        orientation === "horizontal" ? "pl-4" : "pt-4",
        className,
      )}
      {...props}
    />
  );
}

function CarouselPrevious({
  className,
  variant = "outline",
  size = "icon-md",
  icon: Icon = ChevronLeft,
  screenReaderLabel = "Previous slide",
  ...props
}: React.ComponentProps<typeof Button> & {
  icon?: LucideIcon;
  screenReaderLabel?: string;
}) {
  const { orientation, scrollPrev, canScrollPrev } = useCarousel();
  const isInFooter = React.useContext(CarouselFooterContext)?.isInFooter;

  const buttonClassName = cn(
    "qodo qodo-carousel-previous size-8 rounded-md p-1.5",
    isInFooter
      ? "shrink-0"
      : orientation === "horizontal"
        ? "absolute top-1/2 -left-12 -translate-y-1/2"
        : "absolute -top-12 left-1/2 -translate-x-1/2 rotate-90",
    className,
  );

  return (
    <Button
      data-slot="carousel-previous"
      variant={variant}
      size={size}
      className={buttonClassName}
      disabled={!canScrollPrev}
      onClick={scrollPrev}
      {...props}
    >
      <Icon className="stroke-icon-action-neutral-idle" />
      <span className="sr-only">{screenReaderLabel}</span>
    </Button>
  );
}

function CarouselNext({
  className,
  variant = "outline",
  size = "icon-md",
  icon: Icon = ChevronRight,
  screenReaderLabel = "Next slide",
  ...props
}: React.ComponentProps<typeof Button> & {
  icon?: LucideIcon;
  screenReaderLabel?: string;
}) {
  const { orientation, scrollNext, canScrollNext } = useCarousel();
  const isInFooter = React.useContext(CarouselFooterContext)?.isInFooter;

  const buttonClassName = cn(
    "qodo qodo-carousel-next size-8 rounded-md shrink-0 p-1.5",
    isInFooter
      ? "shrink-0"
      : orientation === "horizontal"
        ? "absolute top-1/2 -right-12 -translate-y-1/2"
        : "absolute -bottom-12 left-1/2 -translate-x-1/2 rotate-90",
    className,
  );

  return (
    <Button
      data-slot="carousel-next"
      variant={variant}
      size={size}
      className={buttonClassName}
      disabled={!canScrollNext}
      onClick={scrollNext}
      {...props}
    >
      <Icon className="stroke-icon-action-neutral-idle" />
      <span className="sr-only">{screenReaderLabel}</span>
    </Button>
  );
}

function CarouselFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <CarouselFooterContext.Provider value={{ isInFooter: true }}>
      <div
        className={cn("flex items-center justify-center gap-6 pt-2", className)}
        {...props}
      />
    </CarouselFooterContext.Provider>
  );
}

function CarouselIndicator({
  className,
  children,
  index,
  isCurrent,
  ...props
}: React.ComponentProps<typeof Button> & {
  index: number;
  isCurrent: boolean;
}) {
  const { scrollTo } = useCarousel();
  const buttonClassName = cn(
    "text-xs uppercase",
    isCurrent
      ? "text-text-general-neutral-strong!"
      : "text-text-general-neutral-mild!",
    className,
  );

  return (
    <Button
      variant="ghost"
      className={buttonClassName}
      disabled={isCurrent}
      onClick={() => scrollTo(index)}
      {...props}
    >
      {children ?? `Step ${index + 1}`}
    </Button>
  );
}

export {
  type CarouselApi,
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
  CarouselIndicator,
  CarouselFooter,
};
